import React, { useEffect, useMemo } from 'react';
import useSWR from 'swr';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Printer, Loader2, AlertCircle } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { formatEuro, formatDate } from '@/lib/format';
import { IMPORT_TYPES } from '@/lib/estimating/templates';
import { getImport, getLines } from '@/lib/estimating/service';
import { lineColumns } from '@/components/estimating/ImportPreview';
import { resolveDocumentBrand } from '@/lib/branding';
import { BrandedDocument } from '@/components/documents/BrandedDocument';
import { Button } from '@/components/ui/button';
import '@/styles/document.css';

async function getProjectIdentity(projectId) {
  const { data, error } = await supabase
    .from('projects')
    .select('id, name, project_code, client_name, site_address, business_units(name, legal_entity_name, unit_type), organisations!projects_organisation_id_fkey(name)')
    .eq('id', projectId).single();
  if (error) throw error;
  return data;
}

const DOC_TITLES = { priced_boq: 'Priced Bill of Quantities', tender_summary: 'Tender Summary', cost_budget: 'Internal Cost Budget' };

export default function EstimatePrint() {
  const { id: projectId, importId } = useParams();
  const { data: imp, error, isLoading } = useSWR(['estimate-import', importId], () => getImport(importId));
  const { data: lines } = useSWR(imp ? ['estimate-lines', imp.id] : null, () => getLines(imp));
  const { data: project } = useSWR(['project-identity', projectId], () => getProjectIdentity(projectId));
  const cols = useMemo(() => (imp ? lineColumns(imp.import_type) : []), [imp]);
  const identity = useMemo(() => resolveDocumentBrand({ businessUnit: project?.business_units, organisation: project?.organisations }), [project]);

  useEffect(() => { if (imp && project) document.title = `${DOC_TITLES[imp.import_type]} v${imp.version_number} — ${project.project_code}`; return () => { document.title = 'Alvora OS'; }; }, [imp, project]);

  if (isLoading || !project) return <div className="p-8 flex items-center gap-2 text-sm text-muted-foreground"><Loader2 size={16} className="animate-spin" /> Preparing document…</div>;
  if (error || !imp) return <div className="p-8 text-sm text-red-800 flex items-center gap-2"><AlertCircle size={16} /> {error?.message || 'Not accessible.'}</div>;

  const t = IMPORT_TYPES[imp.import_type];
  const totalKey = t.totalKey;
  const total = (lines || []).reduce((a, l) => a + (Number(l[totalKey]) || 0), 0);
  const isTender = imp.import_type === 'tender_summary';
  const confidential = imp.import_type === 'cost_budget';

  return (
    <div className="alvora-doc-page" data-testid="estimate-print-page">
      <div className="alvora-doc-toolbar print:hidden">
        <Link to={`/projects/${projectId}/estimating/${importId}`} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-charcoal"><ArrowLeft size={14} /> Back</Link>
        <Button type="button" size="sm" data-testid="document-print-button" onClick={() => window.print()} className="bg-charcoal hover:bg-charcoal-soft text-ivory"><Printer size={14} className="mr-1" /> Print / Save as PDF</Button>
      </div>
      <BrandedDocument
        identity={identity}
        title={DOC_TITLES[imp.import_type]}
        subtitle={`${project.name} · ${project.project_code}`}
        confidential={confidential}
        meta={[
          ['Client', project.client_name], ['Site', project.site_address],
          ['Version', `v${imp.version_number}${imp.is_original ? ' (original)' : ''}`], ['Status', imp.status],
          ['Confirmed', imp.confirmed_at ? formatDate(imp.confirmed_at) : '—'], ['Source file', imp.file_name],
        ]}
        footerNote={`${DOC_TITLES[imp.import_type]} v${imp.version_number} · generated ${formatDate(new Date())}`}
      >
        <table className="alvora-doc__table">
          <thead><tr>{cols.map((c) => <th key={c.key} className={c.num ? 'num' : ''}>{c.header}</th>)}</tr></thead>
          <tbody>
            {(lines || []).map((l) => (
              <tr key={l.id}>{cols.map((c) => <td key={c.key} className={c.num ? 'num' : ''}>{c.render ? c.render(l) : c.num ? (c.key === 'quantity' ? (l[c.key] ?? '') : formatEuro(l[c.key])) : (l[c.key] ?? '')}</td>)}</tr>
            ))}
          </tbody>
          <tfoot>
            {isTender ? (
              <>
                <tr><td colSpan={cols.length - 1}>Subtotal ex VAT</td><td className="num">{formatEuro(imp.tender_subtotal_ex_vat)}</td></tr>
                <tr><td colSpan={cols.length - 1}>VAT</td><td className="num">{formatEuro(imp.tender_vat_amount)}</td></tr>
                <tr className="grand"><td colSpan={cols.length - 1}>Tender total incl. VAT</td><td className="num">{formatEuro(imp.tender_total_inc_vat)}</td></tr>
              </>
            ) : (
              <tr className="grand"><td colSpan={cols.length - 1}>{imp.import_type === 'priced_boq' ? 'Total ex VAT' : 'Total direct cost'}</td><td className="num">{formatEuro(imp.computed_total ?? total)}</td></tr>
            )}
          </tfoot>
        </table>
      </BrandedDocument>
    </div>
  );
}
